/**
 * src/services/wsBase.ts
 *
 * Resolve a browser WebSocket URL for a backend hub path (e.g. `/ws/map`).
 *
 * In local dev the CRA dev server (:3366) does not reliably proxy WebSocket
 * upgrades — webpack-dev-server owns the HTTP `upgrade` event for its own HMR
 * socket, so `/ws/agents`, `/ws/mavlink`, and `/ws/map` intermittently fail
 * with "closed before the connection is established". So in dev we connect
 * straight to the Node backend on :8080, exactly mirroring the HTTP dev
 * pattern used across the app (contract.ts, v2/config.ts, agentService.ts, …).
 *
 * In production the SPA is served by the Node server itself, so same-origin
 * (`window.location.host`) already reaches the hubs.
 *
 * Override with REACT_APP_WS_BASE_URL (e.g. `wss://api.example.com`).
 */

/** Node backend port used in local development. */
const DEV_BACKEND_PORT = '8080';

/**
 * Close a WebSocket without the "closed before the connection is established"
 * console error. Calling `.close()` on a socket that is still `CONNECTING`
 * (common under React 18 StrictMode's dev-only mount→unmount→remount) makes
 * Chrome log that warning. Defer the close until the socket opens instead.
 */
export function safeCloseWs(ws: WebSocket | null | undefined): void {
  if (!ws) return;
  if (ws.readyState === WebSocket.CONNECTING) {
    ws.addEventListener(
      'open',
      () => { try { ws.close(); } catch { /* swallow */ } },
      { once: true },
    );
    return;
  }
  try { ws.close(); } catch { /* swallow */ }
}

/** Build a WS/WSS URL for a backend hub path, dev-aware. */
export function resolveWsUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  const override = process.env.REACT_APP_WS_BASE_URL;
  if (override) return `${override.replace(/\/+$/, '')}${p}`;

  const host = window.location.hostname;
  const isDev = host === 'localhost' || host === '127.0.0.1';
  if (isDev) return `${proto}//${host}:${DEV_BACKEND_PORT}${p}`;

  return `${proto}//${window.location.host}${p}`;
}
